import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Rss, Play, Heart, Volume2, VolumeX, Send, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const LiveFeed = () => {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [mutedVideos, setMutedVideos] = useState({});
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchPosts();
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/feed/posts?limit=10`);
      setPosts(response.data.slice(0, 8));
    } catch (error) {
      setPosts([
        { id: '1', user: { username: 'PartyPete' }, bar_name: 'The Blue Lounge', content: 'This place is packed tonight!', created_at: new Date().toISOString(), likes: 12 },
        { id: '2', user: { username: 'NightOwl' }, bar_name: 'Neon Nights', content: 'Great DJ spinning!', created_at: new Date(Date.now() - 300000).toISOString(), likes: 8 },
        { id: '3', user: { username: 'CocktailQueen' }, bar_name: 'Rooftop Bar', content: 'Happy hour specials are amazing', created_at: new Date(Date.now() - 600000).toISOString(), likes: 15 },
        { id: '4', user: { username: 'BarHopper' }, bar_name: 'The Irish Pub', content: 'Live music tonight!', created_at: new Date(Date.now() - 900000).toISOString(), likes: 22 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    if (!user) { toast.error('Log in to post'); return; }
    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/feed/posts`, { content: newPost }, { headers: { Authorization: `Bearer ${token}` } });
      setNewPost('');
      fetchPosts();
      toast.success('Posted!');
    } catch {
      // Add locally if API not available
      setPosts(prev => [{
        id: Date.now().toString(),
        user: { username: user.username || 'You' },
        bar_name: '',
        content: newPost,
        created_at: new Date().toISOString(),
        likes: 0
      }, ...prev]);
      setNewPost('');
      toast.success('Posted!');
    } finally {
      setPosting(false);
    }
  };

  const shareToTwitter = (post) => {
    const text = `${post.content}${post.bar_name ? ` @ ${post.bar_name}` : ''} #NightBuZz`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const toggleMute = (postId) => {
    setMutedVideos(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  if (loading) {
    return (
      <Card className="glass p-4 border border-slate-700 mb-4">
        <div className="flex items-center gap-2 text-purple-400">
          <Rss className="w-4 h-4 animate-pulse" />
          <span className="text-sm">Loading...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass p-4 border border-purple-500/30 mb-4" data-testid="live-feed-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Rss className="w-5 h-5 text-purple-400" />
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
        </div>
        <h3 className="font-bold text-white text-sm" style={{ fontFamily: 'Space Grotesk' }}>X</h3>
        <span className="text-xs text-slate-400">live from the scene</span>
      </div>

      {/* Post input */}
      {user && (
        <div className="flex gap-2 mb-3">
          <input
            data-testid="crackin-post-input"
            type="text"
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePost()}
            placeholder="What's on your mind?"
            className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
          />
          <button
            data-testid="crackin-post-btn"
            onClick={handlePost}
            disabled={posting || !newPost.trim()}
            className="w-9 h-9 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center text-white disabled:opacity-40 transition-all shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Posts */}
      <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
        {posts.map((post) => (
          <div
            key={post.id}
            className="p-2.5 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-purple-500/30 transition-all"
          >
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {post.user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-sm font-semibold text-white">{post.user?.username || 'Anonymous'}</span>
                  {post.bar_name && (
                    <span className="text-xs text-purple-400 truncate">@ {post.bar_name}</span>
                  )}
                </div>

                {post.video_url ? (
                  <div className="relative rounded-lg overflow-hidden mb-1.5 max-w-[280px]">
                    <video
                      src={post.video_url}
                      className="w-full aspect-video object-cover bg-slate-900 rounded-lg"
                      controls={playingVideo === post.id}
                      muted={mutedVideos[post.id] !== false}
                      playsInline
                      onClick={() => setPlayingVideo(playingVideo === post.id ? null : post.id)}
                    />
                    {playingVideo !== post.id && (
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer"
                        onClick={() => setPlayingVideo(post.id)}
                      >
                        <Play className="w-10 h-10 text-white/80" fill="white" />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-300 mb-1.5">{post.content || post.caption}</p>
                )}

                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                  <span className="flex items-center gap-0.5 hover:text-pink-400 cursor-pointer">
                    <Heart className="w-3 h-3" /> {post.likes || 0}
                  </span>
                  <button
                    data-testid={`share-twitter-${post.id}`}
                    onClick={(e) => { e.stopPropagation(); shareToTwitter(post); }}
                    className="flex items-center gap-0.5 hover:text-blue-400 transition-colors"
                  >
                    <Twitter className="w-3 h-3" /> Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default LiveFeed;
